import i18n from "../../i18n";
import { AxiosError } from "axios";
import {
  AccessApiErrorBody,
  AccessDeniedError,
  ApiError,
  ApiErrorResponse,
  ApiErrorBody,
  ApiErrorCode,
  ErrorType,
  GolBaseError,
  ApiResourceCodeError,
} from "./error-types";
import { buildRouteWithParams } from "../../helpers/build-route-with-params";
import { config } from "../../config";

export class ApiErrorImpl implements ApiError, GolBaseError {
  response?: any;
  status: number;
  code: ApiErrorCode;
  data?: ApiErrorBody;

  constructor(error: AxiosError<ApiErrorResponse>) {
    this.response = error.response;
    this.code = ApiErrorCode.unknown;
    this.status = -1;

    if (error.response) {
      this.status = error.response.status;
      this.data = error.response.data?.error;
      let errorCode = this.data?.code;
      if (errorCode) {
        this.code =
          ApiErrorCode[errorCode as keyof typeof ApiErrorCode] ??
          ApiErrorCode.unknown;
      }
    }
  }

  getRedirectPath() {
    return buildRouteWithParams(config.routes.selection, false);
  }

  getErrorType() {
    return ErrorType.error;
  }

  getMessage() {
    return i18n.t(this.getMessagKey());
  }

  getMessagKey() {
    switch (this.code) {
      case ApiErrorCode.restaurant_blank:
      case ApiErrorCode.restaurant_id_missing: {
        return `instabook.errors.connection_error`;
      }
      default: {
        return `instabook.errors.${this.code}`;
      }
    }
  }
}

export class ValidationErrorApiErrorImpl extends ApiErrorImpl {
  public _transKey: string = "invalid_data";

  set transKey(key: string) {
    this._transKey = key;
  }

  get transKey(): string {
    return this._transKey;
  }

  getMessagKey() {
    let errorKey = this.getResourcesInError()[0];
    if (errorKey === "code") {
      errorKey = this.code;
    }

    switch (errorKey) {
      case ApiResourceCodeError.AlreadyOnWaitingList: {
        return this.response.data?.error[errorKey][0];
      }
      case ApiResourceCodeError.NoMoreMenuAvailable:
      case ApiResourceCodeError.NoMenuLeft:
      case ApiResourceCodeError.MenuSelectionRequested: {
        return this.response.data?.error.menu[0];
      }
      case ApiResourceCodeError.VoucherDetails: {
        return this.response.data?.error.voucher_details[0];
      }
      default: {
        return `instabook.errors.${this.transKey}`;
      }
    }
  }

  getVoucherDetailsError(): String | undefined {
    if (
      this.getResourcesInError().includes(ApiResourceCodeError.VoucherDetails)
    ) {
      return ApiResourceCodeError.VoucherDetails;
    } else if (
      this.getResourcesInError().includes(
        ApiResourceCodeError.LimitVouchersNumber
      )
    ) {
      return ApiResourceCodeError.LimitVouchersNumber;
    }
    return undefined;
  }

  getResourcesInError(): Array<string> {
    if (this.data) {
      return Object.keys(this.data);
    }
    return [];
  }

  getRedirectPath() {
    if (
      [
        ApiErrorCode.capture_failed,
        ApiErrorCode.payment_refused,
        ApiErrorCode.setup_intent_invalid,
        ApiErrorCode.stripe_customer_creation_failed,
      ].includes(this.code)
    ) {
      return buildRouteWithParams(config.routes.confirmation);
    } else if (ApiErrorCode.no_menu_left === this.code) {
      return buildRouteWithParams(config.routes.commercials);
    } else {
      let resources = this.getResourcesInError();
      if (resources.includes("contact_info")) {
        return buildRouteWithParams(config.routes.confirmation);
      }
    }
    return buildRouteWithParams(config.routes.selection);
  }
}
export class ResourceNotFoundApiErrorImpl extends ApiErrorImpl {
  getMessagKey() {
    switch (this.code) {
      case ApiErrorCode.menu_unfound: {
        return "menu unfound";
      }
      case ApiErrorCode.runtime_commercial_unfound: {
        return "instabook.errors.runtime_commercial_unfound";
      }
      case ApiErrorCode.restaurant_blank: {
        return `instabook.errors.connection_error`;
      }
      default: {
        return `instabook.errors.session_expired`;
      }
    }
  }

  getRedirectPath() {
    switch (this.code) {
      case ApiErrorCode.runtime_commercial_unfound:
        return buildRouteWithParams(config.routes.commercials);
      default:
        return super.getRedirectPath();
    }
  }
}

export class BadRequestApiErrorImpl extends ApiErrorImpl {
  constructor(error: AxiosError<ApiErrorResponse>) {
    super(error);
    this.code = ApiErrorCode.bad_request;
  }

  getMessagKey() {
    switch (this.data?.code) {
      case ApiErrorCode.pending_booking_creation_failed: {
        return i18n.t(`instabook.errors.error`);
      }
      default: {
        if (this.response.data.error.message) {
          return this.response.data.error.message;
        }
        return `instabook.errors.error`;
      }
    }
  }
}

export class AccessDeniedApiErrorImpl
  extends ApiErrorImpl
  implements AccessDeniedError
{
  getMessage() {
    if (this.isPartnerNameNeeded()) {
      return i18n.t(this.getMessagKey(), { partner_name: this.partnerName() });
    }
    return super.getMessage();
  }

  partnerName() {
    return (this.data as AccessApiErrorBody)?.partner_name ?? "";
  }

  isPartnerNameNeeded() {
    return (
      this.code === ApiErrorCode.access_denied ||
      this.code === ApiErrorCode.partnership_disabled ||
      this.code === ApiErrorCode.partnership_not_yet_created
    );
  }
}

export class RuntimeErrorImpl extends Error implements GolBaseError {
  constructor() {
    super();
    this.message = i18n.t(
      "instabook.messages.booking_not_available_for_people_table_engine"
    );
  }

  getRedirectPath() {
    return buildRouteWithParams(config.routes.selection);
  }
  getMessage() {
    return this.message;
  }

  getErrorType() {
    return ErrorType.warning;
  }
}

export class UpdateFromBookingErrorImpl implements ApiError, GolBaseError {
  status: number;
  code: ApiErrorCode;
  message: string;

  constructor(message: string) {
    this.status = 422;
    this.code = ApiErrorCode.update_from_booking_error;
    this.message = message;
  }

  getMessage() {
    return this.message;
  }

  getErrorType() {
    return ErrorType.error;
  }
}

export class SetupIntentApiErrorImpl extends ApiErrorImpl {
  getMessagKey() {
    return "instabook.errors.refresh_on_payment";
  }

  getRedirectPath() {
    return buildRouteWithParams(config.routes.selection);
  }
}
